<template>
  <v-card outlined class="ticket-activity">
    <div class="pa-4 pb-3 d-flex align-center">
      <v-avatar size="40" :color="statusColor(ticket.status)" class="mr-3">
        <v-icon dark>{{ statusIcon(ticket.status) }}</v-icon>
      </v-avatar>
      <div>
        <div class="text-caption text-uppercase grey--text">Statut</div>
        <div class="subtitle-1 font-weight-medium">{{ statusLabel(ticket.status) }}</div>
      </div>
    </div>

    <div v-if="canReopen || canClose" class="px-4 pb-3 d-flex">
      <v-btn v-if="canReopen" small depressed color="primary" class="mr-2" @click="$emit('status', 'open')">
        <v-icon left small>mdi-restore</v-icon>Rouvrir
      </v-btn>
      <v-btn v-if="canClose" small outlined @click="$emit('status', 'closed')">
        <v-icon left small>mdi-check-circle-outline</v-icon>Fermer
      </v-btn>
    </div>

    <div v-if="$slots.default" class="px-4 pb-2"><slot /></div>

    <template v-if="ticket.events.length">
      <v-divider />
      <div class="pa-4 pb-0 text-caption text-uppercase grey--text">Activité</div>
      <v-timeline dense align-top class="ticket-timeline pr-4">
        <v-timeline-item v-for="e in events" :key="e.id" small fill-dot :color="eventColor(e)" :icon="eventIcon(e)">
          <div class="text-body-2">{{ eventLabel(e) }}</div>
          <div class="text-caption grey--text">
            {{ e.actor ? e.actor.username + ' · ' : '' }}{{ formatDate(e.created_at) }}
          </div>
        </v-timeline-item>
      </v-timeline>
    </template>
  </v-card>
</template>

<script>
import { statusColor, statusLabel, formatDate } from '@/utils/support'

const STATUS_ICONS = {
  open: 'mdi-inbox-arrow-down',
  in_progress: 'mdi-progress-wrench',
  waiting_user: 'mdi-account-clock-outline',
  resolved: 'mdi-check',
  closed: 'mdi-lock-outline',
}
const PRIORITY = { low: 'Basse', normal: 'Normale', high: 'Haute' }
const EVENT_ICONS = {
  created: 'mdi-plus',
  assigned: 'mdi-account-arrow-right-outline',
  priority_changed: 'mdi-flag-outline',
  link_added: 'mdi-link-variant-plus',
  link_removed: 'mdi-link-variant-off',
}

/** Status header, user actions (reopen / close), optional staff controls (default slot) and event timeline. */
export default {
  name: 'TicketActivityCard',
  props: {
    ticket: { type: Object, required: true },
    /** Show the author's Rouvrir / Fermer actions (hidden in staff mode, which uses selects). */
    userActions: { type: Boolean, default: false },
  },
  computed: {
    canReopen() {
      return this.userActions && this.ticket.status === 'resolved'
    },
    canClose() {
      return this.userActions && this.ticket.is_author && this.ticket.status !== 'closed'
    },
    /** Newest first. */
    events() {
      return [...this.ticket.events].reverse()
    },
  },
  methods: {
    statusColor,
    statusLabel,
    formatDate,
    statusIcon: (s) => STATUS_ICONS[s] || 'mdi-help',
    eventIcon(e) {
      return e.kind === 'status_changed' ? STATUS_ICONS[e.data.to] || 'mdi-swap-horizontal' : EVENT_ICONS[e.kind] || 'mdi-circle-small'
    },
    eventColor(e) {
      return e.kind === 'status_changed' ? statusColor(e.data.to) : 'grey'
    },
    eventLabel(e) {
      if (e.kind === 'created') return 'Ticket créé'
      if (e.kind === 'status_changed') return `Statut : ${statusLabel(e.data.to)}`
      if (e.kind === 'priority_changed') return `Priorité : ${PRIORITY[e.data.to] || e.data.to}`
      if (e.kind === 'assigned') return 'Assignation modifiée'
      if (e.kind === 'link_added') return 'Lien ajouté'
      if (e.kind === 'link_removed') return 'Lien retiré'
      return e.kind
    },
  },
}
</script>

<style scoped>
.ticket-timeline {
  padding-top: 8px;
}
</style>
